import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { AnimatePresence, m } from 'motion/react';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import { Gtm } from 'tracking';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import useTest9403 from 'hooks/test/use-test-9403';
import usePickyCatsProducts from 'modules/picky-cats/hooks/use-picky-cats-products';
import PickyCatsFunnel from 'modules/picky-cats/components/funnel';

interface PickyCatsFloatingButtonProps {
  location: string;
}

/** V3 floating entry point for the Picky Cats engagement test: a navy right-edge tab that opens the funnel. */
const PickyCatsFloatingButton: FC<PickyCatsFloatingButtonProps> = ({ location }) => {
  const { formatMessage } = useIntl();
  const { isFloatingButtonFeatureEnabled } = useTest9403();
  const { products, totalCount } = usePickyCatsProducts();
  const [isFunnelOpen, setIsFunnelOpen] = useState(false);

  // Same consistency gate as the need-capture entry point: no products to recommend, no button.
  if (!isFloatingButtonFeatureEnabled || products.length === 0) return null;

  const openFunnel = (): void => {
    Gtm.track.selectElement({ element: ['pickycat', 'floating-button', location] });
    setIsFunnelOpen(true);
  };

  return (
    <LazyMotionWrapper>
      <AnimatePresence>
        {!isFunnelOpen && (
          <m.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="z-floating fixed right-0 bottom-11"
            data-test="picky-cats-floating-button-wrapper"
          >
            <ImpressionTrackerNew element={['pickycat', 'floating-button', location]}>
              <button
                type="button"
                className={cn(
                  'flex items-center justify-end overflow-hidden',
                  'rounded-l-full rounded-r-none px-2.5 py-2',
                  'bg-primary-main text-text-dark-bg-contrast-white',
                  'font-mindset text-center text-sm leading-normal font-normal',
                  'focus-default border-0 shadow-lg'
                )}
                onClick={openFunnel}
                data-test="picky-cats-floating-button"
              >
                <span className="whitespace-nowrap">
                  {formatMessage({ id: 'picky-cats:floating:label' })}
                </span>
              </button>
            </ImpressionTrackerNew>
          </m.div>
        )}
      </AnimatePresence>
      <PickyCatsFunnel
        isOpen={isFunnelOpen}
        onClose={() => setIsFunnelOpen(false)}
        variantName="floating-button"
        location={location}
        products={products}
        totalCount={totalCount}
      />
    </LazyMotionWrapper>
  );
};

export default PickyCatsFloatingButton;
